import type { NitroFetchRequest, NitroFetchOptions } from 'nitropack';
import type { FetchResponse } from 'ofetch';
import { useRuntimeConfig } from '#app/nuxt';
import { useAuth, useUrlWithSubdomain } from '#imports';
import { defu } from 'defu';

export async function useAuthServiceApi<T>(
  request: NitroFetchRequest,
  opts: NitroFetchOptions<NitroFetchRequest> = {},
) {
  const config = useRuntimeConfig();

  const options: Partial<NitroFetchOptions<NitroFetchRequest>> = {
    baseURL: useUrlWithSubdomain(config.public.apiV1BaseUrl),
  };
  return useAuthFetch<T>(request, defu(opts, options));
}

export async function useAuthServiceApiV2<T>(
  request: NitroFetchRequest,
  opts: NitroFetchOptions<NitroFetchRequest> = {},
) {
  const config = useRuntimeConfig();

  const options: Partial<NitroFetchOptions<NitroFetchRequest>> = {
    baseURL: `${useUrlWithSubdomain(config.public.apptegyOIDCUrl)}/api/v2`,
  };
  return useAuthFetch<T>(request, defu(opts, options));
}

export async function useRawAuthServiceApiV2<T>(
  request: NitroFetchRequest,
  opts: NitroFetchOptions<NitroFetchRequest> = {},
) {
  const config = useRuntimeConfig();

  const options: Partial<NitroFetchOptions<NitroFetchRequest>> = {
    baseURL: `${useUrlWithSubdomain(config.public.apptegyOIDCUrl)}/api/v2`,
  };
  return useAuthFetch<T>(request, defu(opts, options), $fetch.raw);
}

export async function useAuthService<T>(request: NitroFetchRequest, opts: NitroFetchOptions<NitroFetchRequest> = {}) {
  const config = useRuntimeConfig();

  const options: Partial<NitroFetchOptions<NitroFetchRequest>> = {
    baseURL: useUrlWithSubdomain(config.public.apptegyOIDCUrl),
  };
  return useAuthFetch<T>(request, defu(opts, options));
}

// Overloads to derive return type from fetch function
export function useAuthFetch<T>(
  request: NitroFetchRequest,
  opts?: NitroFetchOptions<NitroFetchRequest>,
  fetchFn?: typeof $fetch,
): Promise<T>;

export function useAuthFetch<T>(
  request: NitroFetchRequest,
  opts?: NitroFetchOptions<NitroFetchRequest>,
  fetchFn?: typeof $fetch.raw,
): Promise<FetchResponse<T>>;

export async function useAuthFetch<T>(
  request: NitroFetchRequest,
  opts?: NitroFetchOptions<NitroFetchRequest>,
  fetchFn?: typeof $fetch | typeof $fetch.raw,
) {
  const fetch = fetchFn ?? $fetch;
  const options: Partial<NitroFetchOptions<NitroFetchRequest>> = {
    onRequest: (req) => {
      const auth = useAuth();
      req.options.headers.append('Authorization', `Bearer ${auth.idToken.value}`);
    },
  };

  try {
    return await fetch<T>(request, defu(opts ?? {}, options));
  } catch (e) {
    if (e.status === 401) {
      const renewJoinedInflight = isRenewInFlight();
      const pendingRenew = renewSession();
      await trackAuthError('token_invalid', null, undefined, {
        renew_joined_inflight: renewJoinedInflight,
      });
      const result = await pendingRenew;
      if (result.outcome === 'renewed' || result.outcome === 'recovered') {
        return await fetch(request, defu(opts ?? {}, options));
      }
      throw e;
    }
    throw e;
  }
}
